import { safeSessionStorage } from './safeSessionStorage';

/**
 * Builds a cache key for paginated store data.
 * Format: `${contextName}_${url}` for URL-based caches
 *
 * @param contextName - The context name identifier
 * @param url - The URL used for the cache entry (optional)
 * @returns The cache key string
 */
export const buildPaginatedCacheKey = (
  contextName: string,
  url: string,
): string => {
  return `${contextName}_${url}`;
};

/**
 * Builds a cache key for data store.
 * Format: `${contextName}` for simple data stores
 *
 * @param contextName - The context name identifier
 * @returns The cache key string
 */
export const buildDataStoreCacheKey = (contextName: string): string => {
  return contextName;
};

/**
 * Reads cached entities from sessionStorage for paginated stores.
 * Returns null if cache is invalid or missing.
 *
 * @param contextName - The context name identifier
 * @param url - The URL used for the cache entry
 * @returns Cached entities array or null
 */
export const readCachedEntities = <T>(
  contextName: string,
  url: string,
): T[] | null => {
  const storage = safeSessionStorage();
  const cacheKey = buildPaginatedCacheKey(contextName, url);

  try {
    const cachedDataStr = storage.getItem(cacheKey);
    if (!cachedDataStr) {
      return null;
    }

    const cachedData = JSON.parse(cachedDataStr);
    if (cachedData?.entities && Array.isArray(cachedData.entities)) {
      return cachedData.entities as T[];
    }
  } catch (error) {
    // Invalid cache data - log for debugging but don't throw
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[${contextName}] Failed to read cache for ${url}:`, error);
    }
  }

  return null;
};

/**
 * Writes entities to sessionStorage cache for paginated stores.
 * Silently fails if storage quota is exceeded or storage is unavailable.
 *
 * @param contextName - The context name identifier
 * @param url - The URL used for the cache entry
 * @param entities - The entities array to cache
 */
export const writeCachedEntities = <T>(
  contextName: string,
  url: string,
  entities: T[],
): void => {
  const storage = safeSessionStorage();
  const cacheKey = buildPaginatedCacheKey(contextName, url);

  try {
    storage.setItem(
      cacheKey,
      JSON.stringify({
        entities,
      }),
    );
  } catch (error) {
    // Storage quota exceeded or unavailable - log for debugging but don't throw
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[${contextName}] Failed to write cache for ${url}:`, error);
    }
  }
};

/**
 * Reads cached data from sessionStorage for data stores.
 * Returns null if cache is invalid or missing.
 *
 * @param contextName - The context name identifier
 * @returns Cached data or null
 */
export const readCachedData = <T>(contextName: string): T | null => {
  const storage = safeSessionStorage();
  const cacheKey = buildDataStoreCacheKey(contextName);

  try {
    const cachedDataStr = storage.getItem(cacheKey);
    if (!cachedDataStr) {
      return null;
    }

    return JSON.parse(cachedDataStr) as T;
  } catch (error) {
    // Invalid cache data - log for debugging but don't throw
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[${contextName}] Failed to read cache:`, error);
    }
  }

  return null;
};

/**
 * Writes data to sessionStorage cache for data stores.
 * Silently fails if storage quota is exceeded or storage is unavailable.
 *
 * @param contextName - The context name identifier
 * @param data - The data to cache
 */
export const writeCachedData = <T>(contextName: string, data: T): void => {
  const storage = safeSessionStorage();
  const cacheKey = buildDataStoreCacheKey(contextName);

  try {
    storage.setItem(cacheKey, JSON.stringify(data));
  } catch (error) {
    // Storage quota exceeded or unavailable - log for debugging but don't throw
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[${contextName}] Failed to write cache:`, error);
    }
  }
};

/**
 * Reads cached data from sessionStorage for URL-based stores.
 * Returns null if cache is invalid or missing.
 *
 * @param contextName - The context name identifier
 * @param url - The URL used for the cache entry
 * @returns Cached data or null
 */
export const readCachedDataByUrl = <T>(
  contextName: string,
  url: string,
): T | null => {
  const storage = safeSessionStorage();
  const cacheKey = buildPaginatedCacheKey(contextName, url);

  try {
    const cachedDataStr = storage.getItem(cacheKey);
    if (!cachedDataStr) {
      return null;
    }

    return JSON.parse(cachedDataStr) as T;
  } catch (error) {
    // Invalid cache data - log for debugging but don't throw
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[${contextName}] Failed to read cache for ${url}:`, error);
    }
  }

  return null;
};

/**
 * Writes data to sessionStorage cache for URL-based stores.
 * Silently fails if storage quota is exceeded or storage is unavailable.
 *
 * @param contextName - The context name identifier
 * @param url - The URL used for the cache entry
 * @param data - The data to cache
 */
export const writeCachedDataByUrl = <T>(
  contextName: string,
  url: string,
  data: T,
): void => {
  const storage = safeSessionStorage();
  const cacheKey = buildPaginatedCacheKey(contextName, url);

  try {
    storage.setItem(cacheKey, JSON.stringify(data));
  } catch (error) {
    // Storage quota exceeded or unavailable - log for debugging but don't throw
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[${contextName}] Failed to write cache for ${url}:`, error);
    }
  }
};

/**
 * Removes a cache entry from sessionStorage for URL-based stores.
 * Silently no-ops if the entry does not exist or storage is unavailable
 * (`safeSessionStorage` already swallows underlying storage errors).
 *
 * @param contextName - The context name identifier
 * @param url - The URL used for the cache entry
 */
export const removeCachedDataByUrl = (
  contextName: string,
  url: string,
): void => {
  const storage = safeSessionStorage();
  const cacheKey = buildPaginatedCacheKey(contextName, url);
  storage.removeItem(cacheKey);
};
